import { type FormEvent, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Checkbox, Icon, Select, type Column } from "@legal-500/legal500-design-system";
import {
  powerlistEditionQuestions,
  powerlistQuestions,
  type EditionQuestion,
} from "../../../api/powerlist.service";
import { DataTable } from "../../../components/DataTable/DataTable";
import { ErrorBanner } from "../../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../../components/LoadingState";
import { useQuery } from "../../../hooks/useApi";
import { ROUTES } from "../../../routes";
import { ConfirmModal, FormError, errorMessage, usePowerlistAccess, useToast } from "../shared";
import styles from "../Powerlist.module.css";

type Row = EditionQuestion & { id: string };

export function EditionQuestionsTab({ editionId }: { editionId: string }) {
  const { canWrite, canDelete } = usePowerlistAccess();
  const { data, loading, error, refetch, isRefetching } = useQuery(async () => {
    const [editionQuestions, bank] = await Promise.all([
      powerlistEditionQuestions.list(editionId),
      powerlistQuestions.list(),
    ]);
    return { editionQuestions, bank };
  }, [editionId]);
  const [questionId, setQuestionId] = useState("");
  const [mandatory, setMandatory] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Row | null>(null);
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const { toast, success, failure } = useToast();

  const rows: Row[] = [...(data?.editionQuestions ?? [])]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.text.localeCompare(b.text))
    .map(question => ({ ...question, id: question.questionId }));
  const chosen = new Set(rows.map(row => row.questionId));
  const available = (data?.bank ?? []).filter(
    question => question.isActive && !chosen.has(question.id),
  );

  async function run(action: () => Promise<unknown>, message: string) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await action();
      success(message);
    } catch (actionError) {
      failure(actionError);
    } finally {
      await refetch();
      busyRef.current = false;
      setBusy(false);
    }
  }

  function move(index: number, offset: -1 | 1) {
    const reordered = [...rows];
    const [moving] = reordered.splice(index, 1);
    reordered.splice(index + offset, 0, moving);
    const updates = reordered
      .map((row, sortOrder) => ({ row, sortOrder }))
      .filter(({ row, sortOrder }) => row.sortOrder !== sortOrder);
    void run(async () => {
      for (const { row, sortOrder } of updates) {
        await powerlistEditionQuestions.update(editionId, row.questionId, { sortOrder });
      }
    }, "Question order saved");
  }

  async function handleAdd(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!questionId) {
      setFormError("Choose a question from the bank");
      return;
    }
    setFormError(null);
    try {
      await powerlistEditionQuestions.add(editionId, {
        questionId,
        isMandatory: mandatory,
        sortOrder: rows.length,
      });
      setQuestionId("");
      setMandatory(false);
      success("Question added");
      await refetch();
    } catch (addError) {
      setFormError(errorMessage(addError));
    }
  }

  const columns: Column<Row>[] = [
    ...(canWrite
      ? [
          {
            header: "Order",
            accessor: "sortOrder" as const,
            render: (_value: unknown, row: Row) => {
              const index = rows.findIndex(candidate => candidate.id === row.id);
              return (
                <div className={styles.actions}>
                  <Button
                    variant="icon"
                    size="small"
                    aria-label={`Move "${row.text}" up`}
                    disabled={busy || index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <Icon icon="chevron-up" size="small" />
                  </Button>
                  <Button
                    variant="icon"
                    size="small"
                    aria-label={`Move "${row.text}" down`}
                    disabled={busy || index === rows.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <Icon icon="chevron-down" size="small" />
                  </Button>
                </div>
              );
            },
          },
        ]
      : []),
    { header: "Question", accessor: "text", wrap: true },
    {
      header: "Mandatory",
      accessor: "isMandatory",
      render: (_value: unknown, row: Row) =>
        canWrite ? (
          <Checkbox
            aria-label={`Mandatory: ${row.text}`}
            checked={row.isMandatory}
            disabled={busy}
            onChange={event =>
              void run(
                () =>
                  powerlistEditionQuestions.update(editionId, row.questionId, {
                    isMandatory: event.target.checked,
                  }),
                event.target.checked ? "Marked mandatory" : "Marked optional",
              )
            }
          />
        ) : row.isMandatory ? (
          "Mandatory"
        ) : (
          "Optional"
        ),
    },
    ...(canDelete
      ? [
          {
            header: "Actions",
            accessor: "id" as const,
            render: (_value: unknown, row: Row) => (
              <Button
                variant="outlined"
                color="error"
                size="small"
                aria-label={`Remove "${row.text}"`}
                onClick={() => setRemoving(row)}
              >
                Remove
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <p className={styles.muted}>The interview questions this edition asks, in order.</p>
        <Link to={ROUTES.powerlist.questions}>Manage question bank</Link>
      </div>
      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}
      {loading && data === null ? (
        <LoadingState message="Loading questions..." />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          rowKey="id"
          emptyMessage="This edition asks no questions yet."
        />
      )}
      {canWrite && data && (
        <form className={styles.inlineForm} onSubmit={handleAdd} noValidate>
          <Select
            id="powerlist-edition-question"
            label="Question from the bank"
            value={questionId}
            onChange={event => setQuestionId(event.target.value)}
            options={[
              { value: "", label: available.length ? "Choose a question" : "No more questions" },
              ...available.map(question => ({ value: question.id, label: question.text })),
            ]}
            fullWidth
          />
          <Checkbox
            label="Mandatory"
            aria-label="Mandatory"
            checked={mandatory}
            onChange={event => setMandatory(event.target.checked)}
          />
          <Button type="submit" variant="contained" color="primary" size="small">
            Add question
          </Button>
          <FormError message={formError} />
        </form>
      )}
      <ConfirmModal
        isOpen={removing !== null}
        title="Remove question"
        message={`Stop asking "${removing?.text ?? ""}" in this edition?`}
        confirmLabel="Remove"
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return;
          await powerlistEditionQuestions.remove(editionId, removing.questionId);
          setRemoving(null);
          success("Question removed");
          await refetch();
        }}
      />
      {toast}
    </div>
  );
}
